import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { OnboardingStatus } from '../../features/onboarding/models/organization.dto';
import { SessionService } from '../services/session.service';

/**
 * The one place the app decides where a user belongs.
 *
 * The backend computes `onboarding_status` and ships it on `/users/me/`, so every fact this
 * table used to be assembled from on the client — is the email verified, is there an
 * organization, is the profile filled in — is already folded into a single value. Re-deriving
 * any of them from a second source is how two guards come to disagree, and two guards that
 * disagree about where a user belongs is an infinite redirect, not a merely wrong answer.
 *
 * It is a total `Record` on purpose. Every status names a destination, including the two that
 * mean "onboarding is behind you", so there is no fall-through case where the guard quietly
 * admits a navigation it was supposed to redirect.
 */
export const STEP_ROUTES: Record<OnboardingStatus, string> = {
  auth: '/auth/verify-email',
  workspace: '/onboarding/setup',
  invite_team: '/onboarding/invite-team',
  profile_setup: '/onboarding/setup-profile',
  complete: '/',
  legacy: '/',
  join_project: '/onboarding/join-projects',
  create_project: '/onboarding/create-project',
};

/**
 * A guard that also states which statuses it admits, so `onboarding-routes.spec.ts` can walk the
 * route table and prove that every redirect lands somewhere that accepts the status which
 * produced it. Without that the no-loop property is something a reader has to re-derive by hand
 * from four route files.
 */
export interface OnboardingStatusGuard extends CanActivateFn {
  readonly allowed: readonly OnboardingStatus[];
}

/**
 * Guard factory for routes that only make sense at certain points of onboarding: it admits the
 * navigation when the session's onboarding status is one of `allowed`, and otherwise redirects to
 * the step that status names.
 *
 *     { path: 'invite-team', component: InviteTeamComponent,
 *       canActivate: [onboardingStatusGuard(['invite_team', 'ready'])] }
 *
 * The redirect is *returned* as a `UrlTree` rather than performed with `router.navigate()`.
 * Angular runs a route's `canActivate` guards in parallel (`prioritizedGuardValue`), so a guard
 * that navigates as a side effect and then returns `true` is only racing its siblings — whichever
 * one resumes from its `await` last wins, which is not a decision anybody made. A returned
 * `UrlTree` is the navigation's own answer, and the first non-`true` result in the `canActivate`
 * array is the one the router acts on.
 *
 * It awaits `session.load()` rather than fetching: the app initializer has already fired that
 * request and `load()` hands back the same in-flight promise, so a hard reload still costs one
 * round trip. Without the await the guard would decide on an empty session and redirect every
 * direct navigation back to the start of onboarding.
 *
 * An unknown status — the server never answered, or answered without one — admits the
 * navigation. The alternative is bouncing a user out of the app because the network hiccupped.
 */
export function onboardingStatusGuard(allowed: readonly OnboardingStatus[]): OnboardingStatusGuard {
  const guard: CanActivateFn = async () => {
    const session = inject(SessionService);
    const router = inject(Router);

    await session.load();

    const status = session.onboarding()?.status;
    if (!status || allowed.includes(status)) {
      return true;
    }

    return router.createUrlTree([STEP_ROUTES[status]]);
  };

  return Object.assign(guard, { allowed }) as OnboardingStatusGuard;
}
